import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchApplicantRoundSpots,
  mapApiSpotsToSeatMatrix,
} from "../../../hallticket-lib";
import type { SpotAdmissionRound } from "./spotRoundTypes";
import {
  collectSeatUpdatedOnByKey,
  detectSeatMatrixChanges,
  isUpdatedOnWithinHighlightWindow,
  latestUpdatedOnMs,
  SEAT_HIGHLIGHT_DURATION_MS,
  type SeatCellHighlightMeta,
} from "./seatChangeHighlight";

const AUTO_REFRESH_INTERVAL_MS = 30_000;
const LOG_PREFIX = "[SpotRoundAutoRefresh]";

type TableStatus = "idle" | "loading" | "success" | "error";

type UseSpotRoundAutoRefreshOptions = {
  activeRoundId: string;
  activeRoundStatus: SpotAdmissionRound["status"] | undefined;
  activeViewerCollegeId: string;
  roundsLoadedCollegeId: string;
  now: number;
  apiRoundsRef: React.RefObject<SpotAdmissionRound[]>;
  setActiveRound: React.Dispatch<React.SetStateAction<SpotAdmissionRound | undefined>>;
  setTableStatus: React.Dispatch<React.SetStateAction<TableStatus>>;
  setTableError: React.Dispatch<React.SetStateAction<string>>;
  onAutoRefreshTick?: () => void | Promise<void>;
  onSeatMatrixChanged?: (args: { changedKeys: string[]; detectedAt: number }) => void;
  onSpotUpdatedOnCollected?: (args: {
    collegeId: string;
    latestUpdatedOnMs: number | null;
  }) => void;
};

export function useSpotRoundAutoRefresh({
  activeRoundId,
  activeRoundStatus,
  activeViewerCollegeId,
  roundsLoadedCollegeId,
  now,
  apiRoundsRef,
  setActiveRound,
  setTableStatus,
  setTableError,
  onAutoRefreshTick,
  onSeatMatrixChanged,
  onSpotUpdatedOnCollected,
}: UseSpotRoundAutoRefreshOptions) {
  const [updatedOnByKey, setUpdatedOnByKey] = useState<Map<string, number>>(
    () => new Map(),
  );
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);

  const roundDetailRequestRef = useRef(0);
  const autoRefreshAbortRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<number | null>(null);
  const previousMatrixRef = useRef<SpotAdmissionRound["matrix"] | null>(null);
  const displayedMatrixRef = useRef<{
    roundId: string;
    matrix: SpotAdmissionRound["matrix"];
  } | null>(null);
  const activeRoundIdRef = useRef(activeRoundId);
  activeRoundIdRef.current = activeRoundId;
  const activeViewerCollegeIdRef = useRef(activeViewerCollegeId);
  activeViewerCollegeIdRef.current = activeViewerCollegeId;

  const clearAutoRefreshInterval = useCallback((reason: string) => {
    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
      console.log(`${LOG_PREFIX} interval cleared`, {
        reason,
        activeRoundId: activeRoundIdRef.current || null,
      });
    }
  }, []);

  const abortInFlightAutoRefresh = useCallback((reason: string) => {
    if (autoRefreshAbortRef.current) {
      autoRefreshAbortRef.current.abort();
      autoRefreshAbortRef.current = null;
      console.log(`${LOG_PREFIX} in-flight refresh aborted`, {
        reason,
        activeRoundId: activeRoundIdRef.current || null,
      });
    }
  }, []);

  const resolvePreviousMatrix = useCallback(
    (roundId: string): SpotAdmissionRound["matrix"] | null => {
      if (previousMatrixRef.current) {
        return previousMatrixRef.current;
      }
      const displayed = displayedMatrixRef.current;
      if (displayed?.roundId === roundId) {
        console.log(`${LOG_PREFIX} using displayed matrix as change-detection baseline`, {
          activeRoundId: roundId,
        });
        return displayed.matrix;
      }
      return null;
    },
    [],
  );

  const applyMatrixUpdate = useCallback((roundId: string, matrix: SpotAdmissionRound["matrix"]) => {
    const previous = resolvePreviousMatrix(roundId);
    previousMatrixRef.current = matrix;

    if (!previous) {
      console.log(`${LOG_PREFIX} change detection skipped — no baseline matrix yet`, {
        activeRoundId: roundId,
      });
      return;
    }

    const detectedAt = Date.now();
    const { changedKeys } = detectSeatMatrixChanges(
      previous,
      matrix,
      detectedAt,
      new Map(),
    );
    if (!changedKeys.length) {
      console.log(`${LOG_PREFIX} change detection complete — no seat changes`, {
        activeRoundId: roundId,
      });
      return;
    }

    console.log(`${LOG_PREFIX} change detection complete — updated cells`, {
      activeRoundId: roundId,
      changedCount: changedKeys.length,
      changedKeys,
    });
    onSeatMatrixChanged?.({ changedKeys, detectedAt });
  }, [onSeatMatrixChanged, resolvePreviousMatrix]);

  const loadRoundSpots = useCallback(
    async (
      roundId: string,
      options?: { silent?: boolean; trackChanges?: boolean; signal?: AbortSignal },
    ) => {
      const numericId = Number(roundId);
      if (!Number.isFinite(numericId)) {
        return;
      }

      const silent = options?.silent ?? false;
      const trackChanges = options?.trackChanges ?? silent;
      const requestId = ++roundDetailRequestRef.current;
      const requestCollegeId = activeViewerCollegeIdRef.current;

      if (!silent) {
        setTableStatus("loading");
        setTableError("");
        setActiveRound(undefined);
      }

      console.log(`${LOG_PREFIX} refresh request`, {
        silent,
        trackChanges,
        activeRoundId: roundId,
      });

      try {
        const { spots: spotsResponse, seatsPublished: seatsPublishedFlag } =
          await fetchApplicantRoundSpots(numericId);

        if (requestId !== roundDetailRequestRef.current) {
          return;
        }
        if (options?.signal?.aborted) {
          return;
        }
        if (activeRoundIdRef.current !== roundId) {
          return;
        }
        if (activeViewerCollegeIdRef.current !== requestCollegeId) {
          return;
        }

        console.log(`${LOG_PREFIX} refresh response`, {
          activeRoundId: roundId,
          spotCount: spotsResponse.length,
        });

        const { matrix, courseShortForms } = mapApiSpotsToSeatMatrix(spotsResponse);
        const nextUpdatedOnByKey = collectSeatUpdatedOnByKey(spotsResponse);
        setUpdatedOnByKey(nextUpdatedOnByKey);
        const collectedLatestUpdatedOnMs = latestUpdatedOnMs(nextUpdatedOnByKey);
        onSpotUpdatedOnCollected?.({
          collegeId: requestCollegeId,
          latestUpdatedOnMs: collectedLatestUpdatedOnMs,
        });
        console.log(`${LOG_PREFIX} updatedOn highlights stored`, {
          activeRoundId: roundId,
          collegeId: requestCollegeId || null,
          timestampCount: nextUpdatedOnByKey.size,
          latestUpdatedOnMs: collectedLatestUpdatedOnMs,
        });

        if (trackChanges) {
          applyMatrixUpdate(roundId, matrix);
        } else {
          previousMatrixRef.current = matrix;
          console.log(`${LOG_PREFIX} baseline matrix stored`, {
            activeRoundId: roundId,
            source: silent ? "silent-no-track" : "initial-load",
          });
        }

        displayedMatrixRef.current = { roundId, matrix };

        const apiRoundMeta = apiRoundsRef.current.find((round) => round.id === roundId);
        let seatsPublished = seatsPublishedFlag ?? false;
        if (
          seatsPublishedFlag === null
          && spotsResponse.length === 0
          && apiRoundMeta
        ) {
          seatsPublished =
            apiRoundMeta.status === "Active" || apiRoundMeta.status === "Ended";
        }

        const round: SpotAdmissionRound = apiRoundMeta
          ? {
              ...apiRoundMeta,
              matrix,
              courseShortForms,
              seatsPublished,
            }
          : {
              id: roundId,
              name: `Round ${roundId}`,
              status: "Active",
              dayStart: "",
              dayEnd: "",
              notes: "",
              matrix,
              courseShortForms,
              seatsPublished,
            };

        const refreshedAt = Date.now();
        setLastUpdatedAt(refreshedAt);
        console.log(`${LOG_PREFIX} spots refresh success`, {
          activeRoundId: roundId,
          silent,
          lastUpdatedAt: refreshedAt,
        });

        setActiveRound(round);
        if (!silent) {
          setTableStatus("success");
        }
      } catch (error: unknown) {
        if (requestId !== roundDetailRequestRef.current) {
          return;
        }
        if (options?.signal?.aborted) {
          return;
        }
        if (!silent) {
          setActiveRound(undefined);
          setTableStatus("error");
          setTableError(
            error instanceof Error && error.message
              ? error.message
              : "Unable to load Spot Round seats.",
          );
        }
      }
    },
    [apiRoundsRef, applyMatrixUpdate, onSpotUpdatedOnCollected, setActiveRound, setTableError, setTableStatus],
  );

  const refreshActiveRoundSilently = useCallback(async () => {
    const roundId = activeRoundIdRef.current;
    if (!activeViewerCollegeId || !roundId) {
      return;
    }

    abortInFlightAutoRefresh("new-refresh-tick");
    const controller = new AbortController();
    autoRefreshAbortRef.current = controller;

    try {
      console.log(`${LOG_PREFIX} Spot API refresh`);
      await Promise.all([
        loadRoundSpots(roundId, {
          silent: true,
          trackChanges: true,
          signal: controller.signal,
        }),
        onAutoRefreshTick?.() ?? Promise.resolve(),
      ]);
    } catch {
      // Silent refresh — keep current matrix visible.
    } finally {
      if (autoRefreshAbortRef.current === controller) {
        autoRefreshAbortRef.current = null;
      }
    }
  }, [abortInFlightAutoRefresh, activeViewerCollegeId, loadRoundSpots, onAutoRefreshTick]);

  useEffect(() => {
    setUpdatedOnByKey(new Map());
    previousMatrixRef.current = null;
    displayedMatrixRef.current = null;
    setLastUpdatedAt(null);
    console.log(`${LOG_PREFIX} timer reset`, {
      reason: "college-or-round-changed",
      activeViewerCollegeId: activeViewerCollegeId || null,
      activeRoundId: activeRoundId || null,
    });
    abortInFlightAutoRefresh("college-or-round-changed");
    clearAutoRefreshInterval("college-or-round-changed");
  }, [abortInFlightAutoRefresh, clearAutoRefreshInterval, activeViewerCollegeId, activeRoundId]);

  useEffect(() => {
    const canAutoRefresh = Boolean(
      activeViewerCollegeId
      && activeRoundId
      && roundsLoadedCollegeId === activeViewerCollegeId
      && activeRoundStatus
      && activeRoundStatus !== "Not Started",
    );

    if (!canAutoRefresh) {
      clearAutoRefreshInterval("inactive-round");
      return;
    }

    console.log(`${LOG_PREFIX} auto-refresh started`, {
      activeRoundId,
      intervalMs: AUTO_REFRESH_INTERVAL_MS,
    });
    console.log(`${LOG_PREFIX} active round id`, activeRoundId);

    intervalRef.current = window.setInterval(() => {
      void refreshActiveRoundSilently();
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => {
      clearAutoRefreshInterval("effect-cleanup");
      abortInFlightAutoRefresh("effect-cleanup");
    };
  }, [
    abortInFlightAutoRefresh,
    activeRoundId,
    activeRoundStatus,
    activeViewerCollegeId,
    clearAutoRefreshInterval,
    refreshActiveRoundSilently,
    roundsLoadedCollegeId,
  ]);

  useEffect(
    () => () => {
      clearAutoRefreshInterval("unmount");
      abortInFlightAutoRefresh("unmount");
      roundDetailRequestRef.current += 1;
    },
    [abortInFlightAutoRefresh, clearAutoRefreshInterval],
  );

  const highlightedCellKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const [key, updatedOnMs] of Array.from(updatedOnByKey.entries())) {
      if (isUpdatedOnWithinHighlightWindow(updatedOnMs, now)) {
        keys.add(key);
      }
    }
    if (keys.size > 0) {
      console.log(`${LOG_PREFIX} active highlight keys`, {
        activeRoundId: activeRoundIdRef.current || null,
        count: keys.size,
        keys: Array.from(keys),
      });
    }
    return keys;
  }, [updatedOnByKey, now]);

  const highlightMetaByKey = useMemo(() => {
    const map = new Map<string, SeatCellHighlightMeta>();
    for (const [key, lastChangedAt] of Array.from(updatedOnByKey.entries())) {
      map.set(key, {
        previousValue: "",
        lastChangedAt,
        highlightExpiresAt: lastChangedAt + SEAT_HIGHLIGHT_DURATION_MS,
      });
    }
    return map;
  }, [updatedOnByKey]);

  return {
    loadRoundSpots,
    highlightedCellKeys,
    highlightMetaByKey,
    lastUpdatedAt,
  };
}
